import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import ParameterFieldBase, { IParameterFieldBaseOptions } from '../../../../_common/platform/ts/ParameterFieldBase';
import { IValuesCollection } from '../../ts/appTheme';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import { validators } from '../../../../_common/platform/ts/validators';
import * as DateTimeFunctions from '../../../../_common/platform/ts/DateTimeFunctions';
import {
    getValueFormatFromTheme,
    IOnConverionErrorCallback
} from '../../../../_common/platform/ts/AttrQueryValuesFormat';
import { parseBooleanCustomFormat } from '../../ts/Utilities';

import {
    IFormatElement,
    ATTRIBUTE_TYPES,
    FORMATS,
    TIME_PART_FORMAT_ELEMENTS,
    TIME_PART_TYPES,
    TIME_PART_FORMATS
} from '../../ts/ConfigurationConstants';

import SelectField from '../SelectField';
import NumberField from '../NumberField';
import StringField from '../StringField';
import BooleanField from '../BooleanField';
import { ParameterField } from '../ParameterField';

import { IValueFormat, formatValue } from '../../../../_common/platform/ts/AttrQueryValuesFormat';

const moment = require('pdc-platform-moment');

const DEFAULT_FORMAT = 'default';
const DEFAULT_MAX_LIST_SIZE = 20;

export function isTimeIntervalValueFormat(format: string) {
    return (
        format === 'timeInterval' || // r
        format === 'Hours DD:HH' || // i
        format === 'Hours HH:MM' || // r
        format === 'Hours HH:MM:SS' || // r
        format === 'Minutes DD:HH:MM' || // i
        format === 'Minutes HH:MM:SS' || // r
        format === 'Minutes MM:SS' || // r
        format === 'Minutes HH:MM' || // i
        format === 'Seconds MM:SS' || // i
        format === 'Seconds HH:MM:SS' // i
    );
}

export function isDecimalVisible(attributeType: string, format: string) {
    // VR: returns TRUE if "decimal places" is valid and should be shown: (also, add here 'percent' - WI96751 !
    return (
        (attributeType === ATTRIBUTE_TYPES.REAL ||
            (attributeType === ATTRIBUTE_TYPES.INTEGER &&
                format != null &&
                (format.indexOf('ions') > 0 ||
                    format.indexOf('sands') > 0 ||
                    format.indexOf('large') >= 0 ||
                    format.indexOf('percent') >= 0))) &&
        !isTimeIntervalValueFormat(format) &&
        format !== 'custom' &&
        format !== 'default'
    );
}

function isQuarterFormat(format: string) {
    return (
        format === 'Q' ||
        format === 'latin' ||
        format === 'Qlatin' ||
        format === 'Q_N' ||
        format === 'latin_N' ||
        format === 'Qlatin_N' ||
        format === 'Q_Fiscal' ||
        format === 'latin_Fiscal' ||
        format === 'Qlatin_Fiscal'
    );
}

function isHalfYearFormat(format: string) {
    return (
        format === 'longHalfYear' ||
        format === 'shortHalfYear' ||
        format === 'longHalfYearFiscal' ||
        format === 'shortHalfYearFiscal'
    );
}

function isYearFormat(format: string) {
    return (
        format === 'longYear' ||
        format === 'shortYear' ||
        format === TIME_PART_FORMATS.FOUR_DIGITS ||
        format === TIME_PART_FORMATS.TWO_DIGITS ||
        format === TIME_PART_FORMATS.LONG_FISCAL_YEAR ||
        format === TIME_PART_FORMATS.SHORT_FISCAL_YEAR ||
        format === TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END ||
        format === TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END
    );
}

function isMonthFormat(format: string) {
    return format === 'longMonth' || format === 'shortMonth' || format === 'monthNumber';
}

function isWeekDayFormat(format: string) {
    return (
        format === 'longweekDay' ||
        format === 'shortweekDay' ||
        format === 'minweekDay' ||
        format === 'minWeekDay' ||
        format === 'weekDayNumber'
    );
}

function isSecondsFormat(format: string) {
    //LL: see AQValuesFormat.ts:
    //   case 'Days':  return (value / 60.00 / 60.00 / 24.00).toFixed(2);
    //   case 'Minutes':   return (value / 60.00).toFixed(2);
    return format === 'Days' || format === 'Hours' || format === 'Minutes' || format === 'Seconds';
}

function getYear() {
    return new Date().getFullYear();
}

function SampleField(props: {
    value: IValueFormat;
    attributeType: string;
    timePart: string;
    formatFront: string;
    precision: number;
    customFormat: string;
    useDigitGrouping: boolean;
    formatZeroAsBlank: boolean;
    currency: string;
    useFiscalPrefix: boolean;
    fiscalYearDivider: string;
    theme: IValuesCollection;
}) {
    const [errorText, setErrorText] = React.useState('');

    React.useEffect(() => {
        if (errorText !== '') {
            setErrorText('');
        }
    }, [props.value]);

    const { formatFront, timePart, theme } = props;

    let attributeType = props.attributeType;
    let currency = props.currency;
    let sampleValue: any;
    let newDate: Date;

    switch (attributeType) {
        case ATTRIBUTE_TYPES.INTEGER:
            sampleValue = 12345;

            if (isTimeIntervalValueFormat(formatFront)) {
                sampleValue = 100; // 100 => 01:40  when minutes HH:MM
            } else if (isQuarterFormat(formatFront)) {
                sampleValue = 3;
            } else if (isHalfYearFormat(formatFront)) {
                sampleValue = 2;
            } else if (isYearFormat(formatFront)) {
                sampleValue = getYear();
            } else if (isMonthFormat(formatFront)) {
                sampleValue = 9;
            } else if (isWeekDayFormat(formatFront)) {
                sampleValue = 3;
            } else if (isSecondsFormat(formatFront)) {
                //   case 'Days':  return (value / 60.00 / 60.00 / 24.00).toFixed(2);
                sampleValue = 60 * 60 * 24;
            }
            break;

        case ATTRIBUTE_TYPES.REAL:
            sampleValue = 12345.6789;

            if (isTimeIntervalValueFormat(formatFront)) {
                sampleValue = 44.3333333; // 44.3333333 =>  minutes - 00:44
            } else if (isQuarterFormat(formatFront)) {
                sampleValue = 3;
            } else if (isHalfYearFormat(formatFront)) {
                sampleValue = 2;
            } else if (isYearFormat(formatFront)) {
                sampleValue = getYear();
            } else if (isMonthFormat(formatFront)) {
                sampleValue = 9;
            } else if (isWeekDayFormat(formatFront)) {
                sampleValue = 3;
            } else if (isSecondsFormat(formatFront)) {
                //   case 'Days':  return (value / 60.00 / 60.00 / 24.00).toFixed(2);
                sampleValue = 60 * 60 * 24;
            }
            break;

        case ATTRIBUTE_TYPES.DATE:
            newDate = new Date();
            newDate.setHours(0, 0, 0, 0);
            sampleValue = newDate;
            break;

        case ATTRIBUTE_TYPES.DATETIME:
            newDate = new Date();
            newDate.setHours(21, 30, 15, 0);
            sampleValue = newDate;
            break;

        case ATTRIBUTE_TYPES.TIME:
            newDate = new Date();
            newDate.setHours(21, 30, 15, 0);
            sampleValue = newDate;
            break;

        case ATTRIBUTE_TYPES.BOOLEAN:
            sampleValue = true;
            break;

        default:
            sampleValue = '';
            break;
    }

    if (DateTimeFunctions.isTypeOfAnyDate(attributeType)) {
        if (timePart != null) {
            attributeType = 'number';

            switch (timePart) {
                case TIME_PART_TYPES.YEAR:
                case TIME_PART_TYPES.FISCAL_YEAR:
                    sampleValue = getYear();
                    break;
                case TIME_PART_TYPES.HALF_YEAR:
                case TIME_PART_TYPES.HALF_FISCAL_YEAR:
                    sampleValue = 1;
                    break;
                case TIME_PART_TYPES.QUARTER:
                case TIME_PART_TYPES.FISCAL_QUARTER:
                    sampleValue = moment(new Date()).quarter();
                    break;
                case TIME_PART_TYPES.MONTH:
                    sampleValue = moment(new Date()).month() + 1;
                    break;
                case TIME_PART_TYPES.WEEK:
                    sampleValue = moment(new Date()).week();
                    break;
                case TIME_PART_TYPES.DAY:
                    sampleValue = new Date().getDate();
                    break;
                case TIME_PART_TYPES.DAY_OF_YEAR:
                    sampleValue = 128;
                    break;
                case TIME_PART_TYPES.DAY_OF_WEEK:
                    sampleValue = new Date().getDay() + 1;
                    break;
                case TIME_PART_TYPES.HOUR:
                    sampleValue = new Date().getHours();
                    break;
                case TIME_PART_TYPES.MINUTE:
                    sampleValue = new Date().getMinutes();
                    break;
                case TIME_PART_TYPES.SECOND:
                    sampleValue = new Date().getSeconds();
                    break;
                case TIME_PART_TYPES.MILLISECOND:
                    sampleValue = new Date().getMilliseconds();
                    break;
                default:
                    console.log('Format Chooser: wrong timePart: ' + timePart);
                    sampleValue = 0;
                    break;
            }
        }
    }

    let use24hours: boolean;

    if (theme != null) {
        if (currency == null) {
            currency = theme[themeVars.currencySelector];
        }

        use24hours = theme[themeVars.use24HoursFormat];
    }

    if (currency == null) {
        currency = 'EUR';
    }

    const handleOnConverionError: IOnConverionErrorCallback = (text: string) => {
        if (errorText !== text) {
            setErrorText(text);
        }
    };

    const formattedValue = formatValue(
        sampleValue,
        sampleValue.toString(),
        attributeType,
        {
            formatFront: formatFront,
            precision: props.precision,
            customFormat: props.customFormat,
            currency: currency,
            useDigitGrouping: props.useDigitGrouping,
            formatZeroAsBlank: props.formatZeroAsBlank,
            use24hours: use24hours,
            useFiscalPrefix: props.useFiscalPrefix,
            fiscalYearDivider: props.fiscalYearDivider
        },
        timePart,
        undefined,
        handleOnConverionError
    );

    if (attributeType === ATTRIBUTE_TYPES.BOOLEAN) {
        if (formatFront === 'Checkbox') {
            return <BooleanField value={sampleValue} />;
        } else {
            const falseValue = formatValue(
                false,
                false.toString(),
                attributeType,
                {
                    formatFront: formatFront,
                    precision: props.precision,
                    customFormat: props.customFormat,
                    currency: currency,
                    useDigitGrouping: props.useDigitGrouping,
                    formatZeroAsBlank: props.formatZeroAsBlank,
                    use24hours: use24hours,
                    useFiscalPrefix: props.useFiscalPrefix,
                    fiscalYearDivider: props.fiscalYearDivider
                },
                timePart,
                undefined,
                handleOnConverionError
            );

            return (
                <>
                    <div>{`True  -> '${formattedValue}'`}</div>
                    <div>{`False -> '${falseValue}'`}</div>
                </>
            );
        }
    }

    return sampleValue + " -> '" + (errorText !== '' ? errorText : formattedValue) + "'";
}

function DecimalPlacesSelector(props: {
    value: number;
    attributeType: string;
    format: string;

    onChange(value: number): void;
}) {
    const [precisionErrorMessage, setPrecisionErrorMessage] = React.useState('');

    if (!isDecimalVisible(props.attributeType, props.format)) {
        return null;
    }

    const handleOnDecimalPlacesChange = (value: number) => {
        let errorMessage = '';

        if (value > 20) {
            value = 20;
            errorMessage = 'Maximum allowed decimal places is 20.';
        }

        setPrecisionErrorMessage(errorMessage);
        props.onChange(value);
    };

    let redWarning: React.JSX.Element;

    if (precisionErrorMessage !== '') {
        redWarning = <div style={{ color: 'red' }}>{precisionErrorMessage}</div>;
    }

    return (
        <ParameterField title={'Decimal Places'} clearable={false}>
            <NumberField
                value={props.value}
                allowNull={true}
                integer={true}
                notNegative={true}
                applyOnEnter={true}
                onChange={handleOnDecimalPlacesChange}
            />
            {redWarning}
        </ParameterField>
    );
}

function CurrencySelector(props: {
    value: string;
    formatFront: string;
    theme: IValuesCollection;

    onChange(value: string): void;
}) {
    const [currencyErrorMessage, setCurrencyErrorMessage] = React.useState('');

    if (props.formatFront == null || props.formatFront.indexOf('currency') === -1) {
        return null;
    }

    const handleOnCurrencyChange = (value: string) => {
        let result: string;
        let errorMessage = '';

        if (value === '' || value == null) {
            result = null;
        } else {
            const validatorMessage = validators.string.currency3symbols(value.toUpperCase().trim());

            if (validatorMessage != null) {
                value = 'EUR';
                errorMessage = validatorMessage.join();
            }

            result = value.toUpperCase().trim();
        }

        setCurrencyErrorMessage(errorMessage);
        props.onChange(result);
    };

    const displayValue = props.value || (props.theme != null && props.theme[themeVars.currencySelector]);

    let redWarning: React.JSX.Element;

    if (currencyErrorMessage !== '') {
        redWarning = <div style={{ color: 'red' }}>{currencyErrorMessage}</div>;
    }

    return (
        <ParameterField title={'Select currency'} clearable={false}>
            <StringField
                value={displayValue}
                themeValue={props.theme?.[themeVars.currencySelector] ?? null}
                applyOnEnter={true}
                changeOnBlurOnly={true}
                onChange={handleOnCurrencyChange}
            />
            {redWarning}
        </ParameterField>
    );
}

export interface IFormatChooserValueSelectorOptions extends IParameterFieldBaseOptions {
    attributeType: string;
    timePart?: string;
}

export function FormatChooserValueSelector(props: {
    value?: IValueFormat;
    attributeType: string;
    isScalar: boolean;
    timePart: string;
    theme: IValuesCollection;
    themeObj?: IValuesCollection;
    isCountAggregate?: boolean;

    options?: IFormatChooserValueSelectorOptions;

    getDefaultFormat?(attributeType: string): string;

    onChange(value: IValueFormat): void;
}) {
    const attributeType = props.attributeType || props.options?.attributeType;
    const timePart = props.timePart || props.options?.timePart;
    const isThemeField = props.options != null;
    const theme = props.theme || props.themeObj || (isThemeField && props.options.theme);
    const {
        precision,
        formatFront,
        currency,
        customFormat,
        overwriteInheritedUseDigitGrouping,
        overwriteInheritedFormatZeroAsBlank,
        useFiscalPrefix,
        fiscalYearDivider
    } = props.value || {};

    let { useDigitGrouping, formatZeroAsBlank } = props.value || {};

    const processOnChange = (value: IValueFormat) => {
        let newValue =
            props.value != null
                ? {
                      ...props.value,
                      ...value
                  }
                : value;

        if (newValue.formatFront !== 'custom') {
            delete newValue.customFormat;
        }

        if (isThemeField) {
            // Clean up for Themes.
            // False value of useDigitGrouping must not be deleted,
            // because undefined value does not override True value from parent theme.
            if (newValue.precision == null) {
                delete newValue.precision;
            }

            if (newValue.formatFront == null) {
                delete newValue.formatFront;
            }

            if (newValue.currency == null) {
                delete newValue.currency;
            }

            if (newValue.customFormat == null) {
                delete newValue.customFormat;
            }

            if (_.isEmpty(newValue)) {
                newValue = null;
            }
        }

        props.onChange(newValue);
    };

    const getCustomFormat = (type: string) => {
        if (formatFront === 'custom') {
            if (type === ATTRIBUTE_TYPES.BOOLEAN) {
                const customFormatObj = parseBooleanCustomFormat(customFormat);

                const getOnCustomBooleanFormatChangeHandler = (tag: string) => {
                    return (value: string) => {
                        const customFormatObj = parseBooleanCustomFormat(customFormat);

                        customFormatObj[tag] = value;
                        processOnChange({ customFormat: JSON.stringify(customFormatObj) });
                    };
                };

                return (
                    <>
                        <ParameterField title={'Display for True'} clearable={false}>
                            <StringField
                                value={customFormatObj._true}
                                onChange={getOnCustomBooleanFormatChangeHandler('_true')}
                            />
                        </ParameterField>

                        <ParameterField title={'Display for False'} clearable={false}>
                            <StringField
                                value={customFormatObj._false}
                                onChange={getOnCustomBooleanFormatChangeHandler('_false')}
                            />
                        </ParameterField>
                    </>
                );
            } else {
                const link =
                    type === ATTRIBUTE_TYPES.INTEGER || type === ATTRIBUTE_TYPES.REAL || type === 'count' // ???
                        ? 'http://www.unicode.org/reports/tr35/tr35-numbers.html#Number_Format_Patterns'
                        : 'http://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table';

                return (
                    <ParameterField title={'Custom Format'} clearable={false}>
                        <StringField
                            value={customFormat}
                            onChange={(value) => {
                                if (value === '') {
                                    value = null;
                                }

                                processOnChange({ customFormat: value });
                            }}
                        />
                        <a href={link} target="blank">
                            {'Format Patterns'}
                        </a>
                    </ParameterField>
                );
            }
        }
    };

    const getRemovUseDigitGroupingButton = () => {
        if (overwriteInheritedUseDigitGrouping !== true) {
            return <span className="glyphicon glyphicon-arrow-down" title={'Inherited from parent'}></span>;
        }
        return (
            <span
                className="glyphicon glyphicon-remove"
                onClick={() => {
                    processOnChange({ useDigitGrouping: false, overwriteInheritedUseDigitGrouping: false });
                }}
            ></span>
        );
    };

    const getRemoveFormatZeroAsBlankButton = () => {
        if (overwriteInheritedFormatZeroAsBlank !== true) {
            return <span className="glyphicon glyphicon-arrow-down" title={'Inherited from parent'}></span>;
        }
        return (
            <span
                className="glyphicon glyphicon-remove"
                onClick={() => {
                    processOnChange({ formatZeroAsBlank: false, overwriteInheritedFormatZeroAsBlank: false });
                }}
            ></span>
        );
    };

    const getDigitGrouping = (attributeType: string, format: string) => {
        if (
            format !== 'custom' &&
            ((attributeType === ATTRIBUTE_TYPES.REAL && !isTimeIntervalValueFormat(format)) ||
                (attributeType === ATTRIBUTE_TYPES.INTEGER &&
                    !isTimeIntervalValueFormat(format) &&
                    format !== TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END &&
                    format !== TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END) ||
                attributeType === 'count')
        ) {
            let formatFromTheme = getValueFormatFromTheme(attributeType, theme);

            if (overwriteInheritedUseDigitGrouping !== true) {
                useDigitGrouping = formatFromTheme?.useDigitGrouping;
            }
            return (
                <ParameterField noTitle={true} clearable={false}>
                    {getRemovUseDigitGroupingButton()}

                    <BooleanField
                        value={useDigitGrouping || false}
                        title={'Use Digit Grouping'}
                        onChange={(value) => {
                            processOnChange({ useDigitGrouping: value, overwriteInheritedUseDigitGrouping: true });
                        }}
                    />
                </ParameterField>
            );
        }
    };

    const getFormatZeroAsBlank = (attributeType: string, format: string) => {
        if (
            format !== 'custom' &&
            ((attributeType === ATTRIBUTE_TYPES.REAL && !isTimeIntervalValueFormat(format)) ||
                (attributeType === ATTRIBUTE_TYPES.INTEGER &&
                    !isTimeIntervalValueFormat(format) &&
                    format !== TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END &&
                    format !== TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END) ||
                attributeType === 'count')
        ) {
            let formatFromTheme = getValueFormatFromTheme(attributeType, theme);

            if (formatZeroAsBlank !== true) {
                formatZeroAsBlank = formatFromTheme?.formatZeroAsBlank;
            }
            return (
                <ParameterField noTitle={true} clearable={false}>
                    {getRemoveFormatZeroAsBlankButton()}
                    <BooleanField
                        value={formatZeroAsBlank || false}
                        title={'Format Zero as blank'}
                        onChange={(value) => {
                            processOnChange({ formatZeroAsBlank: value, overwriteInheritedFormatZeroAsBlank: true });
                        }}
                    />
                </ParameterField>
            );
        }
    };

    const getFiscalYearPrefix = (attributeType: string, format: string) => {
        if (
            (attributeType === ATTRIBUTE_TYPES.DATETIME || ATTRIBUTE_TYPES.INTEGER) &&
            (format === TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END ||
                format === TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END)
        ) {
            return (
                <ParameterField noTitle={true} clearable={false}>
                    <BooleanField
                        value={useFiscalPrefix != null ? useFiscalPrefix : true}
                        title={'Use Fiscal Year Prefix (FY)'}
                        onChange={(value) => {
                            processOnChange({ useFiscalPrefix: value });
                        }}
                    />
                </ParameterField>
            );
        }
    };

    const getFiscalYearDivider = (attributeType: string, format: string) => {
        if (
            (attributeType === ATTRIBUTE_TYPES.DATETIME || ATTRIBUTE_TYPES.INTEGER) &&
            (format === TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END ||
                format === TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END)
        ) {
            return (
                <ParameterField title={'Fiscal Year Divider'} clearable={false}>
                    <StringField
                        value={fiscalYearDivider || '-'}
                        onChange={(value) => {
                            if (value === '') {
                                value = null;
                            }

                            processOnChange({ fiscalYearDivider: value });
                        }}
                    />
                </ParameterField>
            );
        }
    };

    const handleOnFormatChange = (value: string) => {
        if (value === DEFAULT_FORMAT) {
            props.onChange(null);
        } else {
            let newPrecision: number = null;

            if (isDecimalVisible(attributeType, value)) {
                newPrecision = precision;
            }

            processOnChange({
                formatFront: value,
                precision: newPrecision
            });
        }
    };

    const isScalar = props.isScalar !== false; // True by default.

    let formatList: IFormatElement[];

    if (props.isCountAggregate) {
        formatList = FORMATS['count'];
    } else if (
        (isScalar && attributeType === ATTRIBUTE_TYPES.BOOLEAN) ||
        attributeType === ATTRIBUTE_TYPES.INTEGER ||
        attributeType === ATTRIBUTE_TYPES.REAL
    ) {
        formatList = FORMATS[attributeType];
    }

    if (DateTimeFunctions.isTypeOfAnyDate(attributeType)) {
        if (timePart != null) {
            formatList = TIME_PART_FORMAT_ELEMENTS[timePart];
        } else {
            if (attributeType === ATTRIBUTE_TYPES.TIME) {
                formatList = TIME_PART_FORMAT_ELEMENTS['type_time'];
            } else if (attributeType === ATTRIBUTE_TYPES.DATE) {
                formatList = TIME_PART_FORMAT_ELEMENTS['type_date_only'];
            } else if (isScalar) {
                formatList = TIME_PART_FORMAT_ELEMENTS['default'];
            }
        }

        if (formatList == null) {
            formatList = TIME_PART_FORMAT_ELEMENTS['not_set'];
        }
    }

    const elements = _.map(formatList, (obj, i) => {
        return (
            <option key={obj.name + '-' + i} value={obj.value} title={obj.title}>
                {obj.name}
            </option>
        );
    });

    let actualFormat = formatFront || 'default';

    if (actualFormat === 'default' && props.getDefaultFormat != null) {
        actualFormat = props.getDefaultFormat(attributeType);
    }

    // +1 for "default" format
    const listSize = !isThemeField
        ? elements.length >= DEFAULT_MAX_LIST_SIZE
            ? DEFAULT_MAX_LIST_SIZE
            : elements.length + 1
        : undefined;
    const formatFromTheme = getValueFormatFromTheme(attributeType, theme);

    if (overwriteInheritedUseDigitGrouping !== true) {
        useDigitGrouping = formatFromTheme?.useDigitGrouping;
    }

    if (formatZeroAsBlank !== true) {
        formatZeroAsBlank = formatFromTheme?.formatZeroAsBlank;
    }

    return (
        <div className="format-chooser-value-selector">
            <div className="left-pane-flex">
                <SelectField value={formatFront} listSize={listSize} onChange={handleOnFormatChange}>
                    <option value={DEFAULT_FORMAT}>{'default'}</option>

                    {elements}
                </SelectField>
            </div>
            <div className="right-pane-flex">
                <label className="mb-1">{'Sample'}</label>
                <div className="sample-value bd-light mb-1">
                    <SampleField
                        value={props.value}
                        attributeType={attributeType}
                        timePart={timePart}
                        formatFront={actualFormat}
                        precision={precision}
                        customFormat={customFormat}
                        useDigitGrouping={useDigitGrouping}
                        formatZeroAsBlank={formatZeroAsBlank}
                        currency={currency}
                        useFiscalPrefix={useFiscalPrefix}
                        fiscalYearDivider={fiscalYearDivider}
                        theme={theme}
                    />
                </div>

                <DecimalPlacesSelector
                    value={precision}
                    attributeType={attributeType}
                    format={actualFormat}
                    onChange={(value) => {
                        processOnChange({ precision: value });
                    }}
                />

                {getCustomFormat(attributeType)}
                {getDigitGrouping(attributeType, actualFormat)}
                {getFormatZeroAsBlank(attributeType, actualFormat)}
                {getFiscalYearPrefix(attributeType, actualFormat)}
                {getFiscalYearDivider(attributeType, actualFormat)}

                <CurrencySelector
                    value={currency}
                    formatFront={formatFront}
                    theme={theme}
                    onChange={(value) => {
                        processOnChange({ currency: value });
                    }}
                />
            </div>
        </div>
    );
}

export class FormatChooserValueSelectorField extends ParameterFieldBase<IFormatChooserValueSelectorOptions> {
    public static EditComponent = FormatChooserValueSelector;

    constructor(glob: IGlob, title: string, options: IFormatChooserValueSelectorOptions) {
        super(glob, title, [], [], null, options);
    }
}
